import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PeriodPicker, StatusBadge, defaultKey, downloadNote, fmtVal, validKey } from "@/components/hr/targetsCommon";

function NoteForm({ tid, onDone }) {
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [fk, setFk] = useState(0);
  const send = async () => {
    const fd = new FormData(); fd.append("note", note); if (file) fd.append("file", file);
    try { await api.post(`/targets/${tid}/notes`, fd); toast.success("Note added"); setNote(""); setFile(null); setFk(fk + 1); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note or evidence description" className="min-w-[220px] flex-1 rounded-md border bg-background px-3 py-1.5 text-sm" data-testid={`target-note-${tid}`} />
      <input key={fk} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0] || null)} className="text-xs" data-testid={`target-file-${tid}`} />
      <Button size="sm" variant="outline" onClick={send} data-testid={`target-note-send-${tid}`}>Add note</Button>
    </div>
  );
}

export function TargetCard({ t, p, onDone, canNote }) {
  const pct = t.unit && p.actual != null && t.target_value ? Math.min(100, Math.round((100 * p.actual) / t.target_value)) : null;
  return (
    <div className="rounded-lg border bg-card p-4" data-testid={`target-card-${t.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium" translate="no">{t.title}</div>
        <StatusBadge s={p.status} id={`target-status-${t.id}`} />
      </div>
      {t.criteria && <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground" translate="no">{t.criteria}</p>}
      <div className="mt-2 text-sm">{t.measure_label} · {`${fmtDate(t.from)} – ${fmtDate(t.to)}`}</div>
      {t.measure !== "manual" && <div className="mt-2 text-sm" data-testid={`target-progress-${t.id}`}>
        {`${fmtVal(p.actual, t.unit)} / ${fmtVal(t.target_value, t.unit)}`} <span className="text-xs text-muted-foreground">{t.direction === "lo" ? "(at most)" : "(at least)"}</span>
        {pct != null && t.direction === "hi" && <div className="mt-1 h-2 w-full rounded bg-muted"><div className="h-2 rounded bg-sky-600" style={{ width: `${pct}%` }} /></div>}
      </div>}
      {p.decision_note && <p className="mt-2 text-sm text-muted-foreground">Decision by <span translate="no">{p.decided_by}</span>: <span translate="no">{p.decision_note}</span></p>}
      {!!p.notes?.length && <ul className="mt-2 space-y-1 text-sm">{p.notes.map((n) => <li key={n.id} className="text-muted-foreground"><span translate="no">{n.note}</span>{n.file && <button type="button" className="ml-2 text-sky-700 underline" onClick={() => downloadNote(n.id, n.file.name)} translate="no">{n.file.name}</button>}</li>)}</ul>}
      {canNote && <NoteForm tid={t.id} onDone={onDone} />}
    </div>
  );
}

export function MyTargets() {
  const [type, setType] = useState("month");
  const [key, setKey] = useState(defaultKey("month"));
  const [d, setD] = useState(null);
  const load = () => api.get("/targets/me", { params: { period_type: type, period_key: key } }).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setD({ targets: [] }); });
  useEffect(() => { if (validKey(type, key)) load(); }, [type, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4" data-testid="my-targets">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-xl text-sm text-muted-foreground">Targets set by the System Admin for your role or for you. Progress is measured automatically from your completed, credited work where possible; manual targets wait for the System Admin's review.</p>
        <PeriodPicker type={type} setType={(v) => { setType(v); setKey(defaultKey(v)); }} pkey={key} setKey={setKey} prefix="my-targets" />
      </div>
      {!d ? <p className="text-sm text-muted-foreground">Loading…</p> : !d.targets.length ? <p className="text-sm text-muted-foreground" data-testid="my-targets-empty">No targets for this period.</p>
        : d.targets.map((t) => <TargetCard key={t.id} t={t} p={t.mine} onDone={load} canNote />)}
    </div>
  );
}
